import React, { useState } from 'react';
import { Modal } from '../components/Modal.js';
import { api } from '../lib/api.js';
import { ServerSummary } from '@kcdash/shared';
import { Server, ShieldAlert, Globe, Database, Sliders, CheckCircle2 } from 'lucide-react';

interface CreateServerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (newServer: ServerSummary) => void;
}

export const CreateServerModal: React.FC<CreateServerModalProps> = ({
  isOpen,
  onClose,
  onCreated,
}) => {
  const [activeTab, setActiveTab] = useState<'basic' | 'network' | 'database'>('basic');

  // Basic settings
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [level, setLevel] = useState<'klaster' | 'trosecko' | 'kutnohorsko'>('klaster');
  const [maxPlayers, setMaxPlayers] = useState(32);
  const [password, setPassword] = useState('');
  const [gamemode, setGamemode] = useState('freeroam.lua');
  const [customGamemode, setCustomGamemode] = useState('');
  const [autoRestart, setAutoRestart] = useState(true);

  // Network settings
  const [port, setPort] = useState(7777);
  const [queryPort, setQueryPort] = useState(1404);
  const [host, setHost] = useState('0.0.0.0');
  const [isPublic, setIsPublic] = useState(true);

  // Database settings (Optional)
  const [dbEnabled, setDbEnabled] = useState(false);
  const [dbDriver, setDbDriver] = useState<'mysql' | 'postgres' | 'sqlite'>('mysql');
  const [dbHost, setDbHost] = useState('127.0.0.1');
  const [dbPort, setDbPort] = useState(3306);
  const [dbUser, setDbUser] = useState('');
  const [dbPassword, setDbPassword] = useState('');
  const [dbName, setDbName] = useState('');

  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const selectedGamemode = gamemode === 'custom' ? (customGamemode.trim() || 'freeroam.lua') : gamemode;

      const created = await api.createServer({
        name,
        description: description || undefined,
        level,
        map: level,
        port: Number(port),
        queryPort: Number(queryPort),
        host: host || '0.0.0.0',
        maxPlayers: Number(maxPlayers),
        gamemode: selectedGamemode,
        password: password || undefined,
        autoRestart,
        databaseDriver: dbEnabled ? dbDriver : '',
        databaseHost: dbEnabled ? dbHost : '',
        databasePort: dbEnabled ? Number(dbPort) : undefined,
        databaseUser: dbEnabled ? dbUser : '',
        databasePassword: dbEnabled ? dbPassword : '',
        databaseName: dbEnabled ? dbName : '',
      });

      onCreated(created);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to create server');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Create New KCD Server" maxWidth="2xl">
      <form onSubmit={handleSubmit} className="space-y-5">
        {error && (
          <div className="p-3 rounded-lg bg-red-950/60 border border-red-800 text-red-300 text-xs flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-red-400 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Wizard Navigation Tabs */}
        <div className="flex border-b border-panel-border gap-2 text-xs font-medium">
          <button
            type="button"
            onClick={() => setActiveTab('basic')}
            className={`flex items-center gap-2 pb-2.5 px-3 border-b-2 transition ${
              activeTab === 'basic'
                ? 'border-brand-500 text-brand-400 font-semibold'
                : 'border-transparent text-neutral-400 hover:text-neutral-200'
            }`}
          >
            <Sliders className="w-3.5 h-3.5" />
            <span>1. Basic</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('network')}
            className={`flex items-center gap-2 pb-2.5 px-3 border-b-2 transition ${
              activeTab === 'network'
                ? 'border-brand-500 text-brand-400 font-semibold'
                : 'border-transparent text-neutral-400 hover:text-neutral-200'
            }`}
          >
            <Globe className="w-3.5 h-3.5" />
            <span>2. Network</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('database')}
            className={`flex items-center gap-2 pb-2.5 px-3 border-b-2 transition ${
              activeTab === 'database'
                ? 'border-brand-500 text-brand-400 font-semibold'
                : 'border-transparent text-neutral-400 hover:text-neutral-200'
            }`}
          >
            <Database className="w-3.5 h-3.5" />
            <span>3. Database (Optional)</span>
          </button>
        </div>

        {/* Tab 1: Basic */}
        {activeTab === 'basic' && (
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-neutral-300 uppercase mb-1">
                Server Name *
              </label>
              <input
                type="text"
                required
                placeholder="e.g. Official Bohemia Freeroam #1"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-panel-input border border-panel-border text-sm text-neutral-200 focus:outline-none focus:border-brand-500"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-neutral-300 uppercase mb-1">
                Description
              </label>
              <input
                type="text"
                placeholder="Optional description shown in dashboard"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-panel-input border border-panel-border text-sm text-neutral-200 focus:outline-none focus:border-brand-500"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-neutral-300 uppercase mb-1">
                  Level / Map *
                </label>
                <select
                  value={level}
                  onChange={(e) => setLevel(e.target.value as any)}
                  className="w-full px-3 py-2 rounded-lg bg-panel-input border border-panel-border text-sm text-neutral-200 focus:outline-none focus:border-brand-500"
                >
                  <option value="klaster">klaster (Monastery area - Default)</option>
                  <option value="trosecko">trosecko (Trosky castle & woods)</option>
                  <option value="kutnohorsko">kutnohorsko (Kutná Hora region)</option>
                </select>
                <p className="text-[11px] text-neutral-500 mt-1">Official KCD:MP levels</p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-neutral-300 uppercase mb-1">
                  Max Players *
                </label>
                <input
                  type="number"
                  min={1}
                  max={512}
                  value={maxPlayers}
                  onChange={(e) => setMaxPlayers(Number(e.target.value))}
                  className="w-full px-3 py-2 rounded-lg bg-panel-input border border-panel-border text-sm text-neutral-200 focus:outline-none focus:border-brand-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-neutral-300 uppercase mb-1">
                  Initial Gamemode *
                </label>
                <select
                  value={gamemode}
                  onChange={(e) => setGamemode(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-panel-input border border-panel-border text-sm text-neutral-200 focus:outline-none focus:border-brand-500"
                >
                  <option value="freeroam.lua">freeroam.lua (Standard official sandbox)</option>
                  <option value="basicrp.lua">basicrp.lua (Basic roleplay starter)</option>
                  <option value="duel_arena.lua">duel_arena.lua (Combat arena)</option>
                  <option value="custom">Custom Lua script filename...</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-neutral-300 uppercase mb-1">
                  Server Password
                </label>
                <input
                  type="password"
                  placeholder="Leave empty for open public server"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-panel-input border border-panel-border text-sm text-neutral-200 focus:outline-none focus:border-brand-500"
                />
              </div>
            </div>

            {gamemode === 'custom' && (
              <div>
                <label className="block text-xs font-semibold text-neutral-300 uppercase mb-1">
                  Custom Gamemode Script Name
                </label>
                <input
                  type="text"
                  placeholder="e.g. my_gamemode.lua"
                  value={customGamemode}
                  onChange={(e) => setCustomGamemode(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-panel-input border border-panel-border text-sm text-neutral-200 focus:outline-none focus:border-brand-500 font-mono"
                />
                <p className="text-[11px] text-neutral-500 mt-1">Must be located inside the server's gamemodes/ folder.</p>
              </div>
            )}

            <div className="flex items-center gap-2 pt-2">
              <input
                type="checkbox"
                id="autoRestartCheck"
                checked={autoRestart}
                onChange={(e) => setAutoRestart(e.target.checked)}
                className="w-4 h-4 rounded bg-panel-input border-panel-border text-brand-600 focus:ring-0"
              />
              <label htmlFor="autoRestartCheck" className="text-xs text-neutral-300">
                Automatically restart server if it crashes unexpectedly
              </label>
            </div>
          </div>
        )}

        {/* Tab 2: Network */}
        {activeTab === 'network' && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-neutral-300 uppercase mb-1">
                  Game Port (UDP/TCP) *
                </label>
                <input
                  type="number"
                  required
                  min={1024}
                  max={65535}
                  value={port}
                  onChange={(e) => setPort(Number(e.target.value))}
                  className="w-full px-3 py-2 rounded-lg bg-panel-input border border-panel-border text-sm text-neutral-200 focus:outline-none focus:border-brand-500 font-mono"
                />
                <p className="text-[11px] text-neutral-500 mt-1">Default KCD:MP port is 7777</p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-neutral-300 uppercase mb-1">
                  Query Port *
                </label>
                <input
                  type="number"
                  required
                  min={1024}
                  max={65535}
                  value={queryPort}
                  onChange={(e) => setQueryPort(Number(e.target.value))}
                  className="w-full px-3 py-2 rounded-lg bg-panel-input border border-panel-border text-sm text-neutral-200 focus:outline-none focus:border-brand-500 font-mono"
                />
                <p className="text-[11px] text-neutral-500 mt-1">Default query port is 1404</p>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-neutral-300 uppercase mb-1">
                Listen Hostname / IP
              </label>
              <input
                type="text"
                value={host}
                onChange={(e) => setHost(e.target.value)}
                placeholder="0.0.0.0"
                className="w-full px-3 py-2 rounded-lg bg-panel-input border border-panel-border text-sm text-neutral-200 focus:outline-none focus:border-brand-500 font-mono"
              />
              <p className="text-[11px] text-neutral-500 mt-1">Use 0.0.0.0 to bind all public network interfaces</p>
            </div>

            <div className="p-3 rounded-lg bg-panel-bg border border-panel-border">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isPublic}
                  onChange={(e) => setIsPublic(e.target.checked)}
                  className="w-4 h-4 rounded bg-panel-input border-panel-border text-brand-600 focus:ring-0"
                />
                <div>
                  <div className="text-xs font-medium text-neutral-200">Public Server Listing</div>
                  <div className="text-[11px] text-neutral-500">Announce this server to KCD master server browser</div>
                </div>
              </label>
            </div>
          </div>
        )}

        {/* Tab 3: Database */}
        {activeTab === 'database' && (
          <div className="space-y-4">
            <div className="p-3 rounded-lg bg-panel-bg border border-panel-border flex items-start gap-3">
              <input
                type="checkbox"
                id="dbEnableCheck"
                checked={dbEnabled}
                onChange={(e) => setDbEnabled(e.target.checked)}
                className="w-4 h-4 mt-0.5 rounded bg-panel-input border-panel-border text-brand-600 focus:ring-0"
              />
              <label htmlFor="dbEnableCheck" className="text-xs">
                <span className="font-semibold text-neutral-200 block">Configure Database for Gamemode</span>
                <span className="text-neutral-400 block text-[11px] mt-0.5">
                  Optional. If unchecked, the server runs in standalone mode without an external SQL database.
                </span>
              </label>
            </div>

            {dbEnabled && (
              <div className="p-3.5 rounded-lg bg-panel-input/40 border border-panel-border space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-neutral-300 uppercase mb-1">
                      Driver
                    </label>
                    <select
                      value={dbDriver}
                      onChange={(e) => setDbDriver(e.target.value as any)}
                      className="w-full px-3 py-2 rounded-lg bg-panel-input border border-panel-border text-xs text-neutral-200 focus:outline-none focus:border-brand-500"
                    >
                      <option value="mysql">MySQL / MariaDB</option>
                      <option value="postgres">PostgreSQL</option>
                      <option value="sqlite">SQLite</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-neutral-300 uppercase mb-1">
                      Database Name
                    </label>
                    <input
                      type="text"
                      placeholder="kcd_gamemode"
                      value={dbName}
                      onChange={(e) => setDbName(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg bg-panel-input border border-panel-border text-xs text-neutral-200 focus:outline-none focus:border-brand-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-2">
                    <label className="block text-xs font-semibold text-neutral-300 uppercase mb-1">
                      Host
                    </label>
                    <input
                      type="text"
                      placeholder="127.0.0.1"
                      value={dbHost}
                      onChange={(e) => setDbHost(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg bg-panel-input border border-panel-border text-xs text-neutral-200 focus:outline-none focus:border-brand-500 font-mono"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-neutral-300 uppercase mb-1">
                      Port
                    </label>
                    <input
                      type="number"
                      value={dbPort}
                      onChange={(e) => setDbPort(Number(e.target.value))}
                      className="w-full px-3 py-2 rounded-lg bg-panel-input border border-panel-border text-xs text-neutral-200 focus:outline-none focus:border-brand-500 font-mono"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-neutral-300 uppercase mb-1">
                      Username
                    </label>
                    <input
                      type="text"
                      placeholder="root"
                      value={dbUser}
                      onChange={(e) => setDbUser(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg bg-panel-input border border-panel-border text-xs text-neutral-200 focus:outline-none focus:border-brand-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-neutral-300 uppercase mb-1">
                      Password
                    </label>
                    <input
                      type="password"
                      placeholder="••••••••"
                      value={dbPassword}
                      onChange={(e) => setDbPassword(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg bg-panel-input border border-panel-border text-xs text-neutral-200 focus:outline-none focus:border-brand-500"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Footer Buttons */}
        <div className="flex items-center justify-between pt-4 border-t border-panel-border">
          <div className="flex items-center gap-1.5 text-xs text-neutral-500">
            <CheckCircle2 className="w-3.5 h-3.5 text-brand-500" />
            <span>KCD:MP V2 Dedicated Server</span>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-xs font-medium text-neutral-400 hover:text-neutral-200 hover:bg-neutral-800 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !name.trim()}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium bg-brand-600 hover:bg-brand-500 text-white transition disabled:opacity-50"
            >
              <Server className="w-4 h-4" />
              <span>{isLoading ? 'Creating Server...' : 'Create Server'}</span>
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
};
